import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import App from './App.jsx'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import { initSentry } from './utils/sentry.js'
import { installNativeApiInterceptor } from './utils/installNativeApiInterceptor.js'
import { initNativeKeyboard } from './utils/initNativeKeyboard.js'

// Initialize Sentry FIRST so it can capture any error during app boot
// (provider initialization, lazy-import hangs, etc.). No-ops cleanly if
// VITE_SENTRY_DSN isn't set in the build environment.
initSentry()

// Install the native API URL interceptor BEFORE React mounts so the very
// first fetch (auth bootstrap, profile load, push registration) on
// Capacitor iOS/Android rewrites relative /api/* paths to absolute
// https://www.vinarai.com/api/* URLs. Without this, on native every
// backend feature silently fails because fetch('/api/...') tries to hit
// the local Capacitor server (capacitor://localhost) which has no
// /api route. Web build is a no-op (isNative gate inside). See
// src/utils/installNativeApiInterceptor.js for the full rationale.
installNativeApiInterceptor()

// VIN-2026-64: enable the native keyboard's accessory bar (the "Done"
// toolbar above the keys) on iOS so text fields have an obvious dismiss
// affordance. Fire-and-forget; web + Android are no-ops. The keyboard
// itself was always the native one — this just surfaces the Done button.
initNativeKeyboard()

// After a Vite deploy the old chunk hashes no longer exist on the CDN.
// This event fires when a dynamic import fails; a hard reload fetches
// the new bundle and resolves the "Failed to fetch dynamically imported
// module" crash without any user action.
//
// VIN-2026-63 hotfix: reload AT MOST ONCE per session. On the native
// WKWebView a chunk preload can fail repeatedly (it's bundled locally,
// not from a CDN, so a reload won't fix it) — the unguarded reload turned
// that into an infinite white-screen flash on launch. One reload still
// recovers the legitimate web case (stale CDN hash after a deploy); the
// guard just stops the loop when reloading can't help.
window.addEventListener('vite:preloadError', (e) => {
  try {
    if (sessionStorage.getItem('vinarai_preload_reloaded') === '1') {
      // Already retried once this session — don't loop. Let the error
      // surface (ErrorBoundary / Suspense) instead of white-flashing.
      e?.preventDefault?.()
      return
    }
    sessionStorage.setItem('vinarai_preload_reloaded', '1')
  } catch { /* sessionStorage blocked — fall through to a single reload */ }
  window.location.reload()
})

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>
)